// @ts-check

import Log from "../core/logging"
// import Registry from "./registry";

import {
  getWith,
  guard,
  maybe,
  maybeNot,
  // flatten,
  // unique,
  // first,
  compact,
  first_compact
  // compose
} from "../core/functions"

const log = new Log()
log.name = "vdom: node:"
// log.silent = false;

// const first_compact = compose (first, compact)

const TEXT_NODE = 3
const DOCUMENT_FRAGMENT_NODE = 11

function refresh() {
  const update = el => {
    if (this.content) {
      el.textContent = this.content
    }

    for (let [key, value] of Object.entries(this.attrs)) {
      if (value) {
        el.setAttribute(key, value)
      } else {
        // should delete attributes not in this.attrs
        el.removeAttribute(key)
      }
    }

    Object.assign(el.style, this.style)
    Object.assign(el.dataset, this.dataset)

    if (["input", "textarea"].contains(this.tag)) {
      el.value = this.value
      el.checked = this.checked
    }
  }

  const newDom = parent => {
    maybe(el => parent.appendChild(el))(this.createElement())
  }

  const addNewDom = () => maybe(newDom)(getWith("dom")(this.parent))

  this.bindNode()

  guard()
    .where(this.nodeType !== TEXT_NODE)
    .do(() => {
      maybe(update)(this.dom)
      maybeNot(addNewDom)(this.dom)
    })
}

function rerender() {
  this.bindNode()
  this.refresh()
  this.children.forEach(c => c.rerender())
}

function update(update) {
  if (update.style) {
    this.style = {
      ...update.style
    }
  }

  if (update.attrs) {
    this.attrs = {
      ...update.attrs
    }
  }

  if (update.dataset) {
    this.dataset = {
      ...update.dataset
    }
  }

  if (update.render) {
    //  always rerender?
    this.rerender()
  }
}

function addEvent(type, handler) {
  if (!this.events[type]) {
    this.events[type] = []
  }

  if (!this.events[type].contains(handler)) {
    this.events[type].push(handler)

    maybe(dom => dom.addEvent(type, handler))(this.dom)
  }
}

function setStyles(styles) {
  this.styles = {
    ...this.style,
    ...styles
  }
}

function getStyle(style) {
  return this.style[style]
}

function setStyle(style, value) {
  this.style[style] = value
}

function addClass(cls) {
  if (!this.attrs.class) {
    this.attrs.class = ""
  }
  const classes = this.attrs.class.split(" ").filter(c => c.length > 0)

  if (classes.indexOf(cls) < 0) {
    classes.push(cls)
    this.attrs.class = classes.join(" ")
  }
}

function removeClass(cls) {
  if (this.attrs.class) {
    this.attrs.class = this.attrs.class
      .split(" ")
      .remove(cls)
      .join(" ")
  }
}

function hasClass(cls) {
  const r = maybe(cls => this.attrs.class.split(" ").contains(cls))(cls)
  return r === true
}

function initElement() {
  const el = document.createElement(this.tag)
  el.textContent = this.content

  maybe(attrs => {
    for (const [key, value] of Object.entries(attrs)) {
      el.setAttribute(key, value)
    }
  })(this.attrs)

  Object.assign(el.dataset, this.dataset)
  Object.assign(el.style, this.style)

  if (this.src != null) {
    el.src = this.src
  }

  for (const [event, handlers] of Object.entries(this.events)) {
    handlers.forEach(handler => el.addEvent(event, handler))
  }

  switch (this.tag) {
    case "label":
    case "th":
      // TODO: this.content gets overwritten, somewhere
      if (this.content) {
        log.info(`setting ${el.content} -> ${this.content}`)
        el.textContent = this.content
      }
      break
    case "input":
    case "textarea":
      el.value = this.value
      el.checked = this.checked
      break
    case "img":
      log.info("adding image:", this.src, this.attrs)
  }

  return el
}

function createElement() {
  let el

  if (this.nodeType === TEXT_NODE) {
    const content = this.nodeValue ? this.nodeValue : this.content
    el = document.createTextNode(content)
  } else {
    el = maybe(() => this.initElement())(this, this.tag)
  }
  return maybe(el => {
    this.dom = el
    // experimental: ugly
    el.component = this
    return el
  })(el)
}

function render(parent) {
  if (!parent) {
    parent = document.createDocumentFragment()
  }

  if (this.skip) {
    detachNode(this)
  } else {
    let el
    if (this.dom) {
      el = this.dom
      if (this.parent && this.parent.dom) {
        if (el.parentNode !== this.parent.dom) {
          // subtree?
          this.parent.dom.appendChild(el)
        }
      }
    } else if (this.tag || this.nodeType) {
      el = this.createElement()
      if (el) {
        parent.appendChild(el)
      }
    } else {
      el = parent
    }
    this.children.forEach(c => c.render(el))
  }
  return parent
}

function sortDOM() {
  if (this.dom) {
    this.children
      .filter(c => c.dom)
      .forEach(c => this.dom.appendChild(c.dom))
  }
}

function append(child) {
  this.children.push(child)
  child.parent = this
  return child
}

function appendChild(tag, attrs, dataset) {
  const child = new Node(tag)
  if (attrs) {
    child.attrs = attrs
    const key = attrs.text ? "text" : "content"
    if (attrs[key]) {
      child.content = attrs[key]
      delete attrs[key]
    }
  }
  if (dataset) {
    child.dataset = dataset
  }
  child.parent = this
  return this.append(child)
}

function getData() {
  // let record
  // if (this.dataLookup) {
  //     record = this.dataLookup()
  // }
  const record = maybe(() => this.dataLookup())(this.dataLookup)

  return record == null ? this.data : record
}

function bindNode() {
  const getRecord = data => (data.record ? data.record : data)

  const formatValue = val => {
    if (this.dataset.format) {
      switch (this.dataset.format.toUpperCase()) {
        case "INTEGER":
          val = Number(val)
          break
        case "NUMERIC":
          val = Number(val).toFixed(2)
          break
        case "CURRENCY":
          val = Number(val).toLocaleString("en-US", {
            minimumFractionDigits: 2
          })
          break
      }
    }
    return val
  }
  const getValue = (record, field) =>
    compact(
      field.split(/\b/).map((key, idx) => {
        let val
        if (record[key]) {
          val = maybe(formatValue)(record[key])
        } else {
          if (idx > 0) {
            val = key
          }
        }
        return val
      })
    ).join(" ")

  const setValue = (record, value) => {
    switch (this.tag.toUpperCase()) {
      case "TEXTAREA":
        this.value = value.replace(/\r?\n/g, "\n")
        break
      case "SELECT":
        break
      case "OPTION": {
        // set option value and text
        this.value = value

        const string = first_compact([
          maybe(string => string)(getWith("dataset", "string")(this)),
          record.string
        ])

        if (string) {
          this.content = string
        }
        break
      }
      case "INPUT":
      case "BUTTON":
        this.value = value
        this.checked = value
        break
      case "IMG":
        if (value !== "") {
          this.attrs.src = value
        }
        break
      case "LABEL":
        break
      default:
        this.content = value
    }
  }

  const hideNode = record => {
    if (record[this.dataset.requires]) {
      this.setStyle("visibility", "visible")
    } else {
      this.setStyle("visibility", "hidden")
    }
  }

  const record = maybe(getRecord)(this.getData())
  // log.warn(`bindNode:`, { ...record });
  maybe(record => {
    maybe(hideNode)(record, this.dataset.requires)
    const value = maybe(getValue)(record, this.dataset.field, this.tag)
    maybe(setValue)(record, value, this.tag)
  })(record)
}

function bindData(record, lookup) {
  const bind = record => {
    maybe(lookup => (this.dataLookup = lookup))(lookup)
    this.bindNode()
    this.children.forEach(c => c.bindData(record, lookup))
  }
  const r = maybe(record => record)(record.record)
  record = r == null ? record : r
  maybe(bind)(record)
}

function getElement(tag) {
  let match
  if (this.tag === tag) {
    match = this
  } else {
    this.children.forEach(c => {
      if (!match) {
        let m = c.getElement(tag)
        if (m != null) {
          match = m
        }
      }
    })
  }

  return match
}

function getElements(tag) {
  return this.listNodes().filter(c => c.tag === tag)
}

function listNodes(list = []) {
  // log.warn (`listnodes called`)
  // maximum call stack exceeded
  //  return flatten([this, this.children.map(c => this.listNodes(c))])
  // list.push (this)
  list = list.concat(this)

  this.children.forEach(c => {
    list = c.listNodes(list)
  })
  return list
}

function removeChild(node) {
  this.children.remove(node)
}

// returns the first dom node in a tree
function findDOM() {
  return this.dom ?
    this.dom :
    first_compact(this.children.map(c => c.findDOM()))
}

// returns the first node with a dom in a tree
function findFirstDOM() {
  return this.dom ?
    this :
    first_compact(this.children.map(c => c.findFirstDOM()))
}

function mountNode(root, node) {
  log.info("mounting node?", root, node)

  const mount = (root, node, dom) => {
    root.appendChild(dom)
    if (dom.nodeType === DOCUMENT_FRAGMENT_NODE) {
      detachNode(node)
    }
  }
  maybe(mount)(root, node, getWith("dom")(node))
}

function getSiblings(node) {
  if (node && node.parent) {
    return node.parent.children
  } else {
    return []
  }
}

function mount(node) {
  const n = this.findFirstDOM()

  const siblings = this.getSiblings(n)

  if (siblings.length > 0) {
    siblings.forEach(s => this.mountNode(node, s))
  } else {
    this.mountNode(node, n)
  }

  this.getElements("style").forEach(s => document.head.appendChild(s.dom))
}

function detachNode(el) {
  if (el.dom != null) {
    el.dom.component = null
    el.dom.remove()
    el.dom = null
  }
}

function unmount() {
  this.children.forEach(c => c.unmount())

  detachNode(this)

  // this.getElements("style").forEach(s => {
  //     detachNode(s);
  // });
}

function attach(node) {
  node.children.push(this)
  this.parent = node

  if (node.dom) {
    if (!this.dom) {
      this.render()
    }
    if (this.dom) {
      this.mountNode(node.dom, this)
    }
  }
}

function replaceNode(node) {
  if (!this.dom) {
    this.render()
  }
  this.parent = node.parent
  this.parent.children.replace(node, this)

  maybe((parent, child) => parent.replaceChild(child, this.dom))(
    this.parent.dom,
    node.dom
  )
}

function findByRole(role) {
  const nodes = this.listNodes()

  return nodes.filter(n =>
    n.dataset.role && n.dataset.role.split(" ").contains(role) ?
    true :
    false
  )
}

const Node = function(tag, attrs = {}) {
  this.tag = tag
  this.content = ""
  this.nodeType = null

  this.value = ""
  this.attrs = attrs
  this.style = {}
  this.dataset = {}
  this.events = []

  this.parent = null
  this.children = []

  this.skip = false
  this.dom = null
  this.template = null

  this.data = null
}

Node.prototype = {
  append: append,

  createElement: createElement,
  initElement: initElement,
  appendElement: appendChild,

  addEventListener: addEvent, // remove?
  addEvent: addEvent,

  //    renderAttributes: renderAttributes,
  //    renderStyle: renderStyle,
  //    getContent: getContent,

  bindData: bindData,
  bindNode: bindNode,

  getElement: getElement,
  getElements: getElements,

  removeChild: removeChild,
  // contains: contains,

  findDOM: findDOM,
  findFirstDOM: findFirstDOM,
  getSiblings: getSiblings,

  mount: mount,
  unmount: unmount,
  mountNode: mountNode,
  replaceNode: replaceNode,

  attach: attach,

  render: render,
  // renderDOM: render,

  update: update, // hmmmm
  rerender: rerender, // hmmmm
  refresh: refresh,
  sortDOM: sortDOM,

  listNodes: listNodes,

  findByRole: findByRole,
  // findByData: findByData,
  // findByDataValue: findByDataValue,

  addClass: addClass,
  removeClass: removeClass,
  hasClass: hasClass,
  setStyles: setStyles,
  setStyle: setStyle,
  getStyle: getStyle,

  getData: getData
}

export default Node